import type { ReactNode } from "react";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal as RNModal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { busyAria } from "../a11y";
import type { UiTheme } from "../theme";

// Full-screen page-sheet chrome — opaque, slide-up, iOS `pageSheet`
// presentation. Encodes the `Cancel · Title · Action` header + optional
// keyboard-avoiding body ONCE for the 9 hand-rolled page-sheets (PlateCalc,
// Feedback, LanguagePicker, OutboxDetail, ExerciseSwapPicker, EntryTip,
// AudioSettings, EditMaxSheet, and ExercisePicker's inline header — see
// next/consolidation.md).
//
// The header title is centred by a symmetric 3-cell flex layout (left/right
// cells grow equally), which ENDS the fixed-`headerSpacer`-width fake-centering
// bug (the disagreeing 32/44/56/64 px spacers `next/consolidation.md` names) —
// no spacer to keep in sync with the Cancel label's width.
//
// Safe-area insets are injected as props (`topInset`/`bottomInset`), not read
// from react-native-safe-area-context — @jackedlogs/ui carries no such dep, so
// the mount feeds useSafeAreaInsets() (same seam as Maxes/EditMaxSheet). Web
// passes none. RN Modal + KeyboardAvoidingView both work under react-native-web,
// so one tree serves mobile and the web-rnw console.

export type PageSheetAction = {
  label: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
};

export type PageSheetProps = {
  theme: UiTheme;
  visible: boolean;
  onRequestClose: () => void;
  title: string;
  /** Leading dismiss affordance label (e.g. "Cancel" / "Close"). */
  cancelLabel: string;
  /** Defaults to onRequestClose. */
  onCancel?: () => void;
  cancelDisabled?: boolean;
  /** Optional trailing action (e.g. Save / Apply). Omit → balanced spacer. */
  action?: PageSheetAction;
  /** Wrap the body in a KeyboardAvoidingView (forms with text input). */
  keyboardAvoiding?: boolean;
  /** Safe-area insets (px) from the mount; 0 on web. */
  topInset?: number;
  bottomInset?: number;
  children: ReactNode;
};

export function PageSheet({
  theme,
  visible,
  onRequestClose,
  title,
  cancelLabel,
  onCancel,
  cancelDisabled = false,
  action,
  keyboardAvoiding = false,
  topInset = 0,
  bottomInset = 0,
  children,
}: PageSheetProps) {
  const styles = makeStyles(theme);
  const body = keyboardAvoiding ? (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
    >
      {children}
    </KeyboardAvoidingView>
  ) : (
    children
  );

  return (
    <RNModal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onRequestClose}
    >
      <View
        style={[
          styles.container,
          { paddingTop: topInset, paddingBottom: bottomInset },
        ]}
      >
        <View style={styles.header}>
          <View style={styles.sideLeft}>
            <Pressable
              accessibilityRole="button"
              onPress={onCancel ?? onRequestClose}
              disabled={cancelDisabled}
              hitSlop={8}
              style={({ pressed }) => [
                styles.hit,
                pressed && !cancelDisabled && styles.pressed,
              ]}
            >
              <Text style={[styles.cancel, cancelDisabled && styles.disabledText]}>
                {cancelLabel}
              </Text>
            </Pressable>
          </View>
          <Text style={styles.title} numberOfLines={1}>
            {title}
          </Text>
          <View style={styles.sideRight}>
            {action ? (
              <Pressable
                accessibilityRole="button"
                // ⚠️ The name has to be DECLARED, not inherited from the child.
                // While `loading` the <Text> below is replaced by a spinner, and
                // a Pressable whose only child is an ActivityIndicator has no
                // accessible name at all — so this button went from "Save" to
                // unnamed exactly when it mattered, on native AND web, across
                // the 9 sheets this primitive backs. <Button> never had this
                // (it declares `accessibilityLabel` outright); this hand-rolled
                // twin did. Found by the a11y audit of RW-38.
                accessibilityLabel={action.label}
                // Same half RW-38 fixed on <Button>; why `aria-*`: see `busyAria`.
                {...busyAria(action.loading)}
                onPress={action.onPress}
                disabled={action.disabled || action.loading}
                hitSlop={8}
                style={({ pressed }) => [
                  styles.hit,
                  pressed && !(action.disabled || action.loading) && styles.pressed,
                ]}
              >
                {action.loading ? (
                  <ActivityIndicator size="small" color={theme.palette.ink2} />
                ) : (
                  <Text
                    style={[styles.action, action.disabled && styles.disabledText]}
                  >
                    {action.label}
                  </Text>
                )}
              </Pressable>
            ) : null}
          </View>
        </View>
        {body}
      </View>
    </RNModal>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: p.bg },
    flex: { flex: 1 },
    header: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s2,
    },
    // Symmetric side cells → the title is centred regardless of the Cancel /
    // action label widths. No fixed spacer to keep in sync.
    sideLeft: { flexGrow: 1, flexBasis: 0, alignItems: "flex-start" },
    sideRight: { flexGrow: 1, flexBasis: 0, alignItems: "flex-end" },
    // ≥44 tap height on the Cancel/action links (the compact label stays,
    // hitSlop widens the rest) + a pressed cue, matching Button/IconButton.
    hit: { minHeight: 44, justifyContent: "center" },
    pressed: { opacity: 0.6 },
    title: {
      flexShrink: 1,
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "600",
      textAlign: "center",
    },
    cancel: { color: p.ink2, fontSize: t.sizes.base },
    // Neutral bold (AA-safe on bg) — not brand500, which fails 4.5:1 on the
    // light ground; the header link reads as primary via weight, not hue
    // (matches the "one orange action" colour budget of the old EditMaxSheet).
    action: { color: p.ink, fontSize: t.sizes.base, fontWeight: "700" },
    // Disabled links drop to ink3 (AA on bg) rather than an opacity dim that
    // sinks below 4.5:1.
    disabledText: { color: p.ink3 },
  });
}
